import { useId, useState, type FormEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { buttonClass } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP, FIELD_LABEL } from "@/components/fieldStyles";
import { formatShortDateTime } from "@/lib/listFormat";
import { NOTE_MAX, type QuoteNote } from "@/lib/submissions/quoteConditions";
import { useSendQuoteNote } from "@/lib/submissions/useSendQuoteNote";

// The notes shared on a quote (PMS-S749, BRK-S362), newest first: Signal's
// ("Signal Specialty") and the broker's own. Signal's internal notes never
// reach the broker, so everything here is shared. A note is never edited: a
// correction is a new one. A new note is added from the quote's Actions
// menu, in its own dialog (BRK-S465), and shows once PMS answers with it.

/** How close to PMS's limit the count shows. */
const COUNT_FROM = NOTE_MAX - 400;

/** The Actions menu's item and its dialog's title, each word capitalized
 *  (Nav, BRK-S465). */
export const ADD_NOTE_LABEL = "Add Note";

/** The quote's notes: this visit's, as PMS answered them, until the quotes
 *  are read again, then PMS's. */
export function QuoteNotes({
  notes,
  added,
}: {
  notes: QuoteNote[];
  added: QuoteNote[];
}) {
  const shown = [
    ...added.filter((a) => !notes.some((n) => n.id === a.id)),
    ...notes,
  ];
  if (shown.length === 0) return <p className="text-brk-dim">No notes yet.</p>;
  return (
    <ul className="flex flex-col gap-2">
      {shown.map((note) => (
        <li
          key={note.id}
          className="flex flex-col gap-1 rounded-lg border border-brk-border p-3"
        >
          <p className="whitespace-pre-line break-words text-brk-text">
            {note.body}
          </p>
          <span className="text-meta text-brk-dim">
            {note.author.name} · {formatShortDateTime(note.createdAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * "Add Note", from the quote popup's Actions menu (BRK-S465): a dialog to
 * share a note with Signal. It closes once PMS keeps the note, which
 * `onAdded` puts at the top of the Notes, and the quotes are read again; a
 * refusal stays in the dialog with the text.
 */
export function AddQuoteNoteDialog({
  submissionId,
  quoteId,
  open,
  onClose,
  onAdded,
  onChanged,
}: {
  submissionId: string;
  quoteId: string;
  open: boolean;
  onClose: () => void;
  onAdded: (note: QuoteNote) => void;
  /** Reads the quotes again, once PMS has the note. */
  onChanged: () => void | Promise<void>;
}) {
  const titleId = useId();
  const fieldId = useId();
  const errorId = useId();
  const [body, setBody] = useState("");
  const { send, saving, problem } = useSendQuoteNote(submissionId, quoteId);

  async function add(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const note = await send(body);
    if (!note) return;
    onAdded(note);
    setBody("");
    onClose();
    void onChanged();
  }

  function close() {
    if (!saving) onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id={titleId}>{ADD_NOTE_LABEL}</DialogTitle>
      <form noValidate onSubmit={add}>
        <DialogContent>
          <div className="flex flex-col gap-1.5 text-sm">
            <label htmlFor={fieldId} className={FIELD_LABEL}>
              Your note
            </label>
            <textarea
              id={fieldId}
              rows={4}
              value={body}
              maxLength={NOTE_MAX}
              onChange={(e) => setBody(e.target.value)}
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? errorId : undefined}
              className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
            />
            <p className={FIELD_HELP}>
              Shared with Signal. Notes can’t be edited: to correct one, add
              another.
              {body.length >= COUNT_FROM &&
                ` ${body.length.toLocaleString("en-US")} of ${NOTE_MAX.toLocaleString("en-US")}.`}
            </p>
            {problem && (
              <p id={errorId} role="alert" className={FIELD_ERROR}>
                {problem}
              </p>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            className={buttonClass({ variant: "secondary" })}
          >
            Not now
          </button>
          <button
            type="submit"
            aria-busy={saving || undefined}
            className={buttonClass()}
          >
            {saving ? "Adding…" : ADD_NOTE_LABEL}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
